// Types for Nida Studios
// These are re-exported from database.types for backward compatibility

// Enum for different content types
export enum ContentType {
  Article = 'article',
  Episode = 'episode'
}

// Enum for media types
export enum MediaType {
  Audio = 'audio',
  Video = 'video',
  Mixed = 'mixed'
}

import type { Role, ContentStatus, EventType } from './services/database.types';
export type { Role, ContentStatus, EventType };

export interface Profile {
  id: string;
  name: string;
  email: string;
  role: Role;
  avatar: string | null; // Unified as avatar
  bio: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Article {
  id: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  coverImage: string | null;
  contentImages?: Array<{url: string; caption?: string}>;
  contentImageUrl?: string | null;
  contentImageCaption?: string | null;
  videoUrl?: string | null;
  videoCaption?: string | null;
  authorId: string | null;
  category: string;
  tags: string[];
  publishedAt: string | null;
  readTime: number;
  isFeatured: boolean;
  isEditorPick: boolean;
  isBreaking: boolean;
  status: ContentStatus;
  slug: string | null;
  createdAt?: string;
  updatedAt?: string;
  type?: 'article';
  comments?: Comment[];
}

export interface Comment {
  id: string;
  articleId: string;
  authorName: string;
  authorEmail?: string | null;
  content: string;
  isApproved: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Show {
  id: string;
  title: string;
  slug?: string | null;
  description: string | null;
  coverImage: string | null;
  hostId: string | null;
  category: string;
  mediaType: MediaType;
  createdAt?: string;
  updatedAt?: string;
  type?: 'show';
}

export interface Episode {
  id: string;
  showId: string | null;
  title: string;
  slug?: string | null;
  description: string | null;
  mediaType: MediaType;
  duration: number;
  publishedAt: string | null;
  uploadedAt?: string;
  fileSize?: number;
  thumbnail: string | null;
  audioUrl: string | null;
  videoUrl: string | null;
  episodeNumber: number | null;
  tags?: string[];
  chapters?: Chapter[];
  highlights?: EpisodeHighlight[];
  guestNames?: string[];
  guestIds?: string[]; // For API payload
  status: ContentStatus;
  createdAt?: string;
  updatedAt?: string;
  type?: 'episode';
}

export interface Chapter {
  title: string;
  start: number;
  end?: number;
}

export interface EpisodeHighlight {
  title: string;
  start: number;
  end: number;
}

export interface ArticleWithAuthor extends Article {
  author: Profile;
}

export interface ShowWithHost extends Show {
  host: Profile;
}

export interface EpisodeWithShow extends Episode {
  show: Show;
  guests?: Profile[];
}

// Legacy types for backward compatibility
export type Author = {
  id: string;
  name: string;
  role: string;
  avatar: string;
};

export type User = {
  id: string;
  name: string;
  email: string;
  role: 'root' | 'admin' | 'editor' | 'journalist' | 'producer';
  avatar: string;
};

// Playback Context Types
export interface PlaybackState {
  currentEpisode: any | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  isMinimized: boolean;
  playbackRate: number;
}

// Page Section Types for Static Pages
export type PageSectionType = 'rich_text' | 'team_grid' | 'value_grid' | 'contact_list';

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  avatar: string;
}

export interface CoreValue {
  title: string;
  description: string;
  icon: string;
}

export interface ContactItem {
  label: string;
  value: string;
  type: 'email' | 'phone' | 'address';
}

export interface PageSection {
  id: string;
  type: PageSectionType;
  title?: string;
  content?: string;
  teamMembers?: TeamMember[];
  values?: CoreValue[];
  contactItems?: ContactItem[];
}

// Feed Item type for homepage
export type FeedItem = {
  id: string;
  title: string;
  excerpt?: string | null;
  description?: string | null;
  coverImage?: string | null;
  thumbnail?: string | null;
  publishedAt: string | null;
  category?: string;
  readTime?: number;
  duration?: number;
  mediaType?: string;
  isFeatured?: boolean;
  isBreaking?: boolean;
  isEditorPick?: boolean;
  status: string;
  type?: string;
  author?: Author | null;
  profiles?: any;
  show?: any;
  shows?: any;
};

// Auth Types
export interface LoginCredentials {
  email?: string;
  password?: string;
}

export interface AuthResponse {
  user: User | null;
  session: { access_token: string } | null;
  error?: string | null;
}

export type AuthUser = User & {
  session?: { access_token: string } | null;
};